import { useMemo } from "react";

import { useAuth } from "../AuthContext";
import { refreshWorkspaceProfile } from "./actions";

// useAuth() as event screens should see it (PLAN.md §5.5 "two profiles"):
// in your own account it's useAuth() itself; in a shared account the plan,
// account type, custom domain and branding are the owner's, and `broadcaster`
// is the owner (event keys, links, subscriptions). Your own profile stays in
// `me`: Profile, Plans, buying, tests, deleting the account and signing out
// always act on you.

// The owner profile fields event screens read.
export const WORKSPACE_PROFILE_FIELDS = [
  "account_type",
  "custom_domain",
  "company_name",
  "company_url",
  "logo_path",
  "no_branding",
  "broadcaster_role",
  "legacy_pricing",
  "registered_datetime",
  "subscription_status",
  "subscription_plan",
  "subscription_access_expiry_datetime",
];

// `event`: views for one event; your own events (tests made while working in
// another account) keep your own profile.
export function workspaceAuthView(auth, ws, event) {
  if (!auth || !ws?.isShared) return auth;
  if (
    event &&
    typeof event === "object" &&
    event.broadcaster &&
    event.broadcaster === ws.self
  ) {
    return auth;
  }
  const profile = ws.profile || {};
  const view = { ...auth };
  for (const field of WORKSPACE_PROFILE_FIELDS) {
    view[field] = profile[field] ?? null;
  }
  // The balance reaches the account's admins only (the server sends null to
  // everyone else); they spend it, buying stays with the holder.
  view.credits = profile.credits ?? null;
  view.broadcaster = ws.owner;
  view.username = ws.owner;
  view.legacyPricing =
    profile.account_type === "business" ||
    !!profile.legacy_pricing ||
    profile.registered_datetime < "2023-07-15";
  view.refetchProfile = async (...args) => {
    const mine = await auth.refetchProfile?.(...args);
    await refreshWorkspaceProfile();
    return mine;
  };
  view.isWorkspace = true;
  view.me = auth.me || auth;
  return view;
}

export default function useWorkspaceAuth() {
  const auth = useAuth();
  const ws = auth?.workspace;
  return useMemo(() => workspaceAuthView(auth, ws), [auth, ws]);
}

export function useEventAuth(event) {
  const auth = useAuth();
  const ws = auth?.workspace;
  const self = event?.broadcaster;
  return useMemo(
    () => workspaceAuthView(auth, ws, self ? { broadcaster: self } : null),
    [auth, ws, self]
  );
}
